// Change #100463 - Back End > Company Details > Subscriptions > Edit (a
// product row) and Resume. Applies to every live licence of that product at
// this company (Super Admin licence update):
//   - price each + VAT %
//   - start date (the nightly card run only bills started licences)
//   - next renewal date (when the card run next collects; grace follows it)
//   - card: the company's primary card, or a specific saved card
//   - billing paused: the card run never invoices or charges it
// Blank date fields keep each licence's own date. Single licences, and who
// holds them, are edited on the Licences tab.

import React, { useEffect, useState } from "react";
import { Input } from "reactstrap";
import { toast } from "react-toastify";

import AppModal from "../../../../Components/Common/AppModal";
import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";

const toDate = (v) => (v && String(v) < "9999" ? String(v).slice(0, 10) : "");
const one = (list, key) => {
  const vals = new Set(list.map((l) => (l[key] == null ? "" : String(l[key]))));
  return vals.size === 1 ? [...vals][0] : null;
};
const pad = (n) => String(n).padStart(2, "0");
const nowStamp = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
const addDays = (date, days) => {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} 00:00:00`;
};

export const cardLabel = (c) =>
  c ? `${c.Brand || "Card"} ending ${c.Last4Digits || "????"}${c.ExpiryMonth ? ` (${pad(c.ExpiryMonth)}/${String(c.ExpiryYear || "").slice(-2)})` : ""}` : "";

export default function EditProductModal({ product, resume = false, hidePrice = false, businessName, cards = [], billingReady, onClose, onDone }) {
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState("");

  useEffect(() => {
    if (!product) return;
    const ls = product.licences || [];
    const vat = one(ls, "AmountVatPercentage");
    const card = one(ls, "BillingMasterPaymentCard_Id");
    const paused = one(ls, "FlagBillingPaused");
    const renewal = one(ls, "RenewalDateTime");
    const end = one(ls, "EndDateTime");
    setForm({
      Price: product.prices?.size === 1 ? [...product.prices][0] : "",
      Vat: vat != null && vat !== "" ? String(Number(vat)) : "20",
      Start: toDate(one(ls, "StartDateTime")),
      Renewal: resume ? new Date().toISOString().slice(0, 10) : toDate(renewal),
      End: end == null ? "" : toDate(end),
      EndMixed: end == null,
      Card: card == null ? "mixed" : card,
      Paused: resume ? false : paused === "1",
    });
    setDirty(resume ? { Renewal: true, Paused: true } : {});
    setConfirm("");
  }, [product, resume]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty((d) => ({ ...d, [k]: true }));
  };

  const save = async () => {
    const each = parseFloat(form.Price);
    const pct = parseFloat(form.Vat) || 0;
    if (dirty.Price || dirty.Vat) {
      if (!Number.isFinite(each) || each < 0) {
        toast.error("Enter the price each (0 for free)");
        return;
      }
    }
    if (dirty.End && form.End && form.Start && form.End <= form.Start) {
      toast.error("The end date must be after the start date");
      return;
    }
    if (dirty.Renewal && !form.Renewal) {
      toast.error("Pick the next renewal date");
      return;
    }
    const v = Math.round((Number.isFinite(each) ? each : 0) * pct) / 100;
    const rows = (product?.licences || []).map((l) => {
      const row = { Id: l.Id };
      if (dirty.Price || dirty.Vat) {
        row.AmountLocalSubTotal = each.toFixed(2);
        row.AmountLocalVat = v.toFixed(2);
        row.AmountLocalTotal = (each + v).toFixed(2);
        row.AmountVatPercentage = pct.toFixed(2);
      }
      if (dirty.Start && form.Start) row.StartDateTime = `${form.Start} 00:00:00`;
      if (dirty.Renewal && form.Renewal) {
        row.RenewalDateTime = `${form.Renewal} 00:00:00`;
        row.RenewalGraceDateTime = addDays(form.Renewal, Number(l.RenewalGraceDays) || 3);
      }
      // Change #100463 - end date: the last day it is billed / gives access.
      if (dirty.End) row.EndDateTime = form.End ? `${form.End} 23:59:59` : "";
      if (billingReady && dirty.Card && form.Card !== "mixed") {
        row.BillingMasterPaymentCard_Id = form.Card === "" ? null : Number(form.Card);
      }
      if (billingReady && dirty.Paused) {
        row.FlagBillingPaused = form.Paused ? 1 : 0;
        row.BillingPausedDateTime = form.Paused ? nowStamp() : null;
      }
      if (confirm) row.FlagConfirmBackdatedRenewal = 1;
      return row;
    });
    if (rows.every((r) => Object.keys(r).length <= (confirm ? 2 : 1))) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const res = await FetchAPI("/subscriptionlicenceadmin/update", "PUT", rows);
      if (Number(res?.status) === 200) {
        toast.success(resume ? `${product.name} resumed` : `${rows.length} × ${product.name} updated`);
        onDone && onDone();
      } else if (res?.RequiresConfirmation) {
        setConfirm(res.message || "Confirm to go ahead.");
      } else {
        toast.error(res?.message || "Could not update the subscription");
      }
    } catch (e) {
      toast.error("Could not update the subscription");
    } finally {
      setSaving(false);
    }
  };

  const qty = product?.licences?.length || 0;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <AppModal
      open={Boolean(product)}
      onClose={() => !saving && onClose()}
      title={resume ? `Resume ${product?.name || ""}` : product?.name || "Subscription"}
      subtitle={`${businessName || ""}${qty ? ` · ${qty} licence${qty === 1 ? "" : "s"}` : ""}`}
      icon={resume ? "ri-play-circle-line" : "ri-pencil-line"}
      headerAccent="indigo"
      size="md"
      onSubmit={save}
      submitLabel={confirm ? "Confirm and save" : resume ? "Resume billing" : "Save"}
      submitDisabled={saving}
      loading={saving}
      loadingText="Saving..."
    >
      <div className="row g-3">
        {!resume && hidePrice && (
          <div className="col-12 text-muted fs-12">
            The plan price is set with Edit (RRP and discount). Here: when it bills, which card, pause and end.
          </div>
        )}
        {!resume && (
          <>
            {!hidePrice && (
            <>
            <div className="col-8">
              <label className="form-label mb-1">Price each (ex VAT)</label>
              <div className="input-group">
                <span className="input-group-text">£</span>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder={product?.prices?.size > 1 ? "Varies - leave blank to keep" : ""}
                  value={form.Price ?? ""}
                  onChange={(e) => set("Price", e.target.value)}
                />
              </div>
            </div>
            <div className="col-4">
              <label className="form-label mb-1">VAT %</label>
              <Input type="number" min={0} step="0.01" value={form.Vat ?? ""} onChange={(e) => set("Vat", e.target.value)} />
            </div>
            </>
            )}
            <div className="col-6">
              <label className="form-label mb-1">Start date</label>
              <Input type="date" value={form.Start || ""} onChange={(e) => set("Start", e.target.value)} />
              <div className="text-muted fs-12 mt-1">Not billed before this date.</div>
            </div>
          </>
        )}
        <div className={resume ? "col-12" : "col-6"}>
          <label className="form-label mb-1">Next renewal</label>
          <Input type="date" value={form.Renewal || ""} onChange={(e) => set("Renewal", e.target.value)} />
          <div className="text-muted fs-12 mt-1">
            {form.Renewal && form.Renewal < today
              ? "In the past: the next run bills every missed month."
              : "When the card is next charged."}
          </div>
        </div>
        {!resume && (
          <div className="col-6">
            <label className="form-label mb-1">End date</label>
            <div className="input-group">
              <Input
                type="date"
                placeholder={form.EndMixed ? "Different end dates - leave blank to keep" : ""}
                value={form.End || ""}
                onChange={(e) => set("End", e.target.value)}
              />
              {form.End && (
                <button type="button" className="btn btn-light" title="No end date" onClick={() => set("End", "")}>
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
            <div className="text-muted fs-12 mt-1">
              {form.End
                ? "Stops after this day: no more renewals are billed and it stops giving access."
                : form.EndMixed && !dirty.End
                ? "These have different end dates; leave blank to keep them."
                : "No end date: it renews until it is ended."}
            </div>
          </div>
        )}
        {!resume && (
          <div className="col-12">
            <label className="form-label mb-1">Charge to</label>
            <Input
              type="select"
              disabled={!billingReady}
              value={form.Card ?? ""}
              onChange={(e) => set("Card", e.target.value)}
            >
              {form.Card === "mixed" && <option value="mixed">Different cards - leave as they are</option>}
              <option value="">The company's primary card</option>
              {cards.map((c) => (
                <option key={c.Id} value={String(c.Id)}>
                  {cardLabel(c)}
                  {Number(c.FlagPrimary) === 1 ? " (primary)" : ""}
                </option>
              ))}
            </Input>
            {!billingReady && (
              <div className="text-muted fs-12 mt-1">Choosing a card and pausing need delta 229 to run first.</div>
            )}
          </div>
        )}
        {!resume && billingReady && (
          <div className="col-12">
            <div className="form-check form-switch mb-0">
              <Input
                type="checkbox"
                className="form-check-input"
                id="ep-paused"
                checked={!!form.Paused}
                onChange={(e) => set("Paused", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="ep-paused">
                Pause billing (the card run will not invoice or charge it)
              </label>
            </div>
          </div>
        )}
        {resume && (
          <div className="col-12">
            <div className="alert alert-info fs-13 mb-0">
              Billing restarts from this date. Choose today or later so the months it was paused are not charged.
            </div>
          </div>
        )}
        {confirm && (
          <div className="col-12">
            <div className="alert alert-warning fs-13 mb-0">{confirm}</div>
          </div>
        )}
      </div>
    </AppModal>
  );
}
